import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { labResultService } from '../services/labResultService'
import { LabResultSummary, LabResultStatus, LabResultReviewStatus, LabResultType } from '../types/labResult'
import { formatDate } from '../utils/locale'
import './LabResults.css'

export function LabResults() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<{
    status?: LabResultStatus[]
    type?: LabResultType[]
    isAbnormal?: boolean
    isCritical?: boolean
    reviewStatus?: LabResultReviewStatus[]
    startDate?: string
    endDate?: string
    search?: string
  }>({})
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const { data: summaries, isLoading, error } = useQuery({
    queryKey: ['lab-results', 'summaries', filters],
    queryFn: () => labResultService.getSummaries(filters),
  })

  const syncMutation = useMutation({
    mutationFn: () => labResultService.syncFromHealthlink(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-results'] })
      alert('Lab results synced from Healthlink successfully')
    },
  })

  const reviewMutation = useMutation({
    mutationFn: (ids: string[]) => labResultService.markMultipleAsReviewed(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-results'] })
      setSelectedIds(new Set())
      alert('Lab results marked as reviewed')
    },
  })

  const handleSelectAll = (checked: boolean) => {
    if (checked && summaries) {
      // Only select rows that can be reviewed (in-review status)
      const reviewableIds = summaries
        .filter((s) => s.reviewStatus === 'in-review')
        .map((s) => s.id)
      setSelectedIds(new Set(reviewableIds))
    } else {
      setSelectedIds(new Set())
    }
  }

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds)
    if (checked) {
      newSelected.add(id)
    } else {
      newSelected.delete(id)
    }
    setSelectedIds(newSelected)
  }

  const handleRowClick = (id: string, summary: LabResultSummary) => {
    // Only allow selection of in-review results
    if (summary.reviewStatus === 'in-review') {
      const isSelected = selectedIds.has(id)
      handleSelectOne(id, !isSelected)
    }
  }

  const handleMarkSelectedAsReviewed = async () => {
    // Filter to only in-review results that can be reviewed
    const inReviewIds = Array.from(selectedIds).filter((id) => {
      const summary = summaries?.find((s) => s.id === id)
      return summary && summary.reviewStatus === 'in-review'
    })
    
    if (inReviewIds.length === 0) {
      alert('Please select at least one in-review result to mark as reviewed')
      return
    }
    
    if (window.confirm(`Mark ${inReviewIds.length} result(s) as reviewed?`)) {
      try {
        await reviewMutation.mutateAsync(inReviewIds)
      } catch (error) {
        alert('Failed to mark results as reviewed')
      }
    }
  }

  const handleSync = async () => {
    if (window.confirm('Sync lab results from Healthlink? This will fetch any new results.')) {
      try {
        await syncMutation.mutateAsync()
      } catch (error: any) {
        alert(error?.message || 'Failed to sync lab results')
      }
    }
  }

  const getStatusBadge = (status: LabResultStatus) => {
    const statusClasses = {
      pending: 'status-pending',
      final: 'status-final',
      preliminary: 'status-preliminary',
      amended: 'status-amended',
      cancelled: 'status-cancelled',
    }
    return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>
  }

  const getTypeLabel = (type: LabResultType): string => {
    const typeLabels: Record<LabResultType, string> = {
      blood: 'Blood',
      'imaging-mri': 'MRI',
      'imaging-ct': 'CT',
      'imaging-xray': 'X-ray',
      'imaging-ultrasound': 'Ultrasound',
      'imaging-echocardiogram': 'Echo',
      pathology: 'Pathology',
      microbiology: 'Microbiology',
      cardiology: 'Cardiology',
      pulmonary: 'Pulmonary',
      endoscopy: 'Endoscopy',
      biopsy: 'Biopsy',
      other: 'Other',
    }
    return typeLabels[type] || type
  }

  if (isLoading) return <div className="loading">Loading lab results...</div>
  if (error) return <div className="error">Error loading lab results</div>

  return (
    <div className="lab-results">
      <div className="lab-results-header">
        <div>
          <h1>Lab Results</h1>
          <p className="subtitle">Laboratory test results from Healthlink and other sources</p>
        </div>
        <div className="header-actions">
          {selectedIds.size > 0 && (
            <button
              className="btn-secondary"
              onClick={handleMarkSelectedAsReviewed}
              disabled={reviewMutation.isPending}
            >
              {reviewMutation.isPending ? 'Marking...' : `Mark ${selectedIds.size} as Reviewed`}
            </button>
          )}
          <button className="btn-primary" onClick={handleSync} disabled={syncMutation.isPending}>
            {syncMutation.isPending ? 'Syncing...' : 'Sync from Healthlink'}
          </button>
        </div>
      </div>

      <div className="lab-results-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Type</label>
            <select
              value={filters.type?.join(',') || ''}
              onChange={(e) => {
                const value = e.target.value
                setFilters({
                  ...filters,
                  type: value ? (value.split(',') as LabResultType[]) : undefined,
                })
              }}
            >
              <option value="">All Types</option>
              <option value="blood">Blood Tests</option>
              <option value="imaging-mri">MRI</option>
              <option value="imaging-ct">CT Scan</option>
              <option value="imaging-xray">X-ray</option>
              <option value="imaging-ultrasound">Ultrasound</option>
              <option value="imaging-echocardiogram">Echocardiogram</option>
              <option value="pathology">Pathology</option>
              <option value="microbiology">Microbiology</option>
              <option value="cardiology">Cardiology</option>
              <option value="pulmonary">Pulmonary</option>
              <option value="endoscopy">Endoscopy</option>
              <option value="biopsy">Biopsy</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status?.join(',') || ''}
              onChange={(e) => {
                const value = e.target.value
                setFilters({
                  ...filters,
                  status: value ? (value.split(',') as LabResultStatus[]) : undefined,
                })
              }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="final">Final</option>
              <option value="preliminary">Preliminary</option>
              <option value="amended">Amended</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Abnormal Results</label>
            <select
              value={filters.isAbnormal === undefined ? '' : filters.isAbnormal ? 'true' : 'false'}
              onChange={(e) => {
                const value = e.target.value
                setFilters({
                  ...filters,
                  isAbnormal: value === '' ? undefined : value === 'true',
                })
              }}
            >
              <option value="">All Results</option>
              <option value="true">Abnormal Only</option>
              <option value="false">Normal Only</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Critical Results</label>
            <select
              value={filters.isCritical === undefined ? '' : filters.isCritical ? 'true' : 'false'}
              onChange={(e) => {
                const value = e.target.value
                setFilters({
                  ...filters,
                  isCritical: value === '' ? undefined : value === 'true',
                })
              }}
            >
              <option value="">All Results</option>
              <option value="true">Critical Only</option>
              <option value="false">Non-Critical Only</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value || undefined })}
            />
          </div>
          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value || undefined })}
            />
          </div>
          <div className="filter-group">
            <label>Review Status</label>
            <select
              value={filters.reviewStatus?.join(',') || ''}
              onChange={(e) => {
                const value = e.target.value
                setFilters({
                  ...filters,
                  reviewStatus: value ? (value.split(',') as LabResultReviewStatus[]) : undefined,
                })
              }}
            >
              <option value="">All Statuses</option>
              <option value="in-review">In Review</option>
              <option value="reviewed">Reviewed</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Order number, accession, lab name..."
              value={filters.search || ''}
              onChange={(e) => setFilters({ ...filters, search: e.target.value || undefined })}
            />
          </div>
        </div>
      </div>

      <div className="lab-results-table-container">
        <table className="lab-results-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={summaries && summaries.length > 0 && summaries.filter((s) => s.reviewStatus === 'in-review').length > 0 && summaries.filter((s) => s.reviewStatus === 'in-review').every((s) => selectedIds.has(s.id))}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  title="Select all reviewable results"
                />
              </th>
              <th>Patient</th>
              <th>Type</th>
              <th>Order Number</th>
              <th>Lab</th>
              <th>Collection Date</th>
              <th>Reported Date</th>
              <th>Panels</th>
              <th>Tests</th>
              <th>Status</th>
              <th>Review</th>
              <th>Flags</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!summaries || summaries.length === 0 ? (
              <tr>
                <td colSpan={13} className="empty-state">
                  No lab results found
                </td>
              </tr>
            ) : (
              summaries.map((summary) => (
                <tr
                  key={summary.id}
                  className={`${summary.isCritical ? 'critical-row' : summary.isAbnormal ? 'abnormal-row' : ''} ${selectedIds.has(summary.id) ? 'selected-row' : ''} ${summary.reviewStatus === 'in-review' ? 'selectable-row' : ''}`}
                  onClick={() => handleRowClick(summary.id, summary)}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(summary.id)}
                      onChange={(e) => handleSelectOne(summary.id, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={summary.reviewStatus === 'reviewed'}
                      title={summary.reviewStatus === 'reviewed' ? 'Already reviewed' : 'Select to mark as reviewed'}
                    />
                  </td>
                  <td>
                    <button
                      className="btn-link"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/patients/${summary.patientId}`)
                      }}
                    >
                      {summary.patientName || summary.patientId}
                    </button>
                  </td>
                  <td>
                    <span className="result-type-badge">{getTypeLabel(summary.type)}</span>
                  </td>
                  <td>{summary.orderNumber || '-'}</td>
                  <td>{summary.labName}</td>
                  <td>{formatDate(summary.collectionDate)}</td>
                  <td>{formatDate(summary.reportedDate)}</td>
                  <td>{summary.panelCount}</td>
                  <td>{summary.testCount}</td>
                  <td>{getStatusBadge(summary.status)}</td>
                  <td>
                    {summary.reviewStatus === 'in-review' && (
                      <span className="review-badge in-review">In Review</span>
                    )}
                    {summary.reviewStatus === 'reviewed' && (
                      <span className="review-badge reviewed">Reviewed</span>
                    )}
                  </td>
                  <td>
                    {summary.isCritical && <span className="flag-badge critical">Critical</span>}
                    {summary.isAbnormal && !summary.isCritical && (
                      <span className="flag-badge abnormal">Abnormal</span>
                    )}
                    {!summary.isAbnormal && !summary.isCritical && (
                      <span className="flag-badge normal">Normal</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn-link"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/lab-results/${summary.id}`)
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

